import { relations } from 'drizzle-orm'
import { index, integer, jsonb, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'

import { user } from '../auth'
import { NotificationAudienceEnum, NotificationCategoryEnum } from '../enums'
import { ContentShare } from './share'

/** Rendering data frozen at emit time so a card survives deletion of its source entity. */
export interface NotificationSnapshotPayload {
  actorName?: string
  actorAvatarUrl?: string
  /**
   * Set only when the reader of this event *is* its actor, so the copy can switch to the
   * second person ("Kamu merekomendasikan ...") instead of naming the reader as a third
   * party. Safe to keep in the shared snapshot because the only events that set it — the
   * `guardian.*` mirrors — are written one per (parent, child) and therefore have exactly
   * one reader. Absent and `false` mean the same thing; nothing writes `false`.
   */
  actorIsRecipient?: boolean
  /**
   * First recipient of the event, for the sender's own ACTOR card
   * ("{recipientName} dan 3 lainnya menerima ... darimu"). Distinct from
   * `actorName`, which always names the sender.
   */
  recipientName?: string
  recipientAvatarUrl?: string
  title?: string
  /**
   * Permanent URL. Only for assets that are genuinely public (`getStaticAssetUrl`).
   * Never store a `getSignedFileUrl` result here — those expire in an hour, and the
   * whole point of the snapshot is that it outlives the source row.
   */
  thumbnailUrl?: string
  /** Durable S3 object key. The feed signs it at read time; preferred over `thumbnailUrl`. */
  thumbnailKey?: string
  /**
   * Member covers for entities whose artwork is a gallery rather than one image — a
   * user-authored `COLLECTION` has no cover of its own and is drawn as a 2x2 grid of its
   * first four member books (`BookGalleryCard`). Durable S3 keys, signed at read time,
   * same rule as `thumbnailKey`. Absent for single-cover entities.
   */
  thumbnailKeys?: string[]
  /** Signed counterparts of `thumbnailKeys`, filled in by the feed query, never stored. */
  thumbnailUrls?: string[]
  /**
   * Sambung Sekolah-Rumah. Frozen at emit time for the same reason as everything else here: the
   * request row may be cancelled or the student removed, and the card still has to read correctly.
   * `actorName` already carries the teacher, so it is not repeated.
   */
  studentName?: string
  /**
   * The child the card is *about*, for notifications a parent receives on their behalf
   * (the `guardian.*` mirrors). The route resolver needs an id, not a name: a parent's
   * card opens that child's profile, and two siblings can share a first name.
   */
  studentId?: string
  /**
   * The child's avatar, drawn in the card's leading slot for `guardian.*` mirrors so a
   * parent with several children can tell two otherwise identical cards apart. Distinct
   * from `actorAvatarUrl`, which names whoever acted at school.
   */
  studentAvatarUrl?: string
  /**
   * The organization this card resolves in, for notifications whose destination is org-scoped
   * (the `guardian.*` mirrors: the family organization the reader is a guardian of). The feed is
   * per user, not per organization, so a card can arrive while another organization is active —
   * a teacher who is also a parent reads both feeds in one — and the click has to know which one
   * to switch to before the destination page can find the child. Absent on cards whose
   * destination is not org-scoped; those navigate as before.
   */
  organizationId?: string
  /** The name of `organizationId`, shown on the card so the reader can tell which context it belongs to. */
  organizationName?: string
  schoolName?: string
  /** The address the request was sent to. Shown to the school, never to other parents. */
  parentEmail?: string
  /** Free text the parent gave when declining, quoted back to the school. */
  rejectionReason?: string
  /**
   * The teacher's own words, for `connect.parent_message`. Free text they typed (the five
   * templates only prefill the box), so it is quoted back verbatim rather than parsed.
   */
  message?: string
  /**
   * Roll-up bookkeeping, not render data — the one field here that is written after emit time.
   *
   * A roll-up event (`premium.access_requested`) counts DISTINCT children, so it has to remember
   * which children it has already counted; `subjectCount` alone cannot tell a second child apart
   * from the same child clicking twice. `emitRollup` reads this set under a row lock, appends the
   * new subject only when it is absent, and republishes `subjectCount` from its length.
   *
   * Bounded by the audience it belongs to — one school's students for one day's bucket — and read
   * by nothing that renders. `studentName` remains the name the copy shows: the first requester.
   */
  studentIds?: string[]
}

/** Secondary context line, e.g. "[Course Name]" beneath the body. */
export interface NotificationContextPayload {
  courseName?: string
  lessonName?: string
}

export const NotificationEvent = pgTable(
  'notification_event',
  {
    id: uuid().primaryKey().defaultRandom(),
    type: text().notNull(),
    // Nullable: cron-generated events have no actor.
    actorId: text().references(() => user.id, { onDelete: 'set null' }),
    entityType: text(),
    entityId: text(),
    shareId: uuid().references(() => ContentShare.id, { onDelete: 'cascade' }),
    snapshot: jsonb().$type<NotificationSnapshotPayload>().notNull().default({}),
    context: jsonb().$type<NotificationContextPayload>().notNull().default({}),
    // Unique when present; cron producers upsert on this instead of duplicating.
    dedupeKey: text(),
    recipientCount: integer().notNull().default(0),
    subjectCount: integer().notNull().default(0),
    readCount: integer().notNull().default(0),
    lastActivityAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index('idx_notification_event_actor').on(table.actorId),
    index('idx_notification_event_share').on(table.shareId),
    unique('uq_notification_event_dedupe_key').on(table.dedupeKey),
  ],
)

export const Notification = pgTable(
  'notification',
  {
    id: uuid().primaryKey().defaultRandom(),
    eventId: uuid()
      .notNull()
      .references(() => NotificationEvent.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    audience: NotificationAudienceEnum().notNull(),
    category: NotificationCategoryEnum().notNull(),
    readAt: timestamp({ mode: 'date', withTimezone: true }),
    seenAt: timestamp({ mode: 'date', withTimezone: true }),
    archivedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    // Primary feed index: keyset pagination scans this.
    index('idx_notification_user_created').on(table.userId, table.createdAt.desc(), table.id.desc()),
    index('idx_notification_user_read').on(table.userId, table.readAt),
    index('idx_notification_user_category').on(table.userId, table.category),
    index('idx_notification_event').on(table.eventId),
    unique('uq_notification_event_user_audience').on(table.eventId, table.userId, table.audience),
  ],
)

export const notificationEventRelations = relations(NotificationEvent, ({ one, many }) => ({
  actor: one(user, { fields: [NotificationEvent.actorId], references: [user.id] }),
  share: one(ContentShare, { fields: [NotificationEvent.shareId], references: [ContentShare.id] }),
  notifications: many(Notification),
}))

export const notificationRelations = relations(Notification, ({ one }) => ({
  event: one(NotificationEvent, { fields: [Notification.eventId], references: [NotificationEvent.id] }),
  user: one(user, { fields: [Notification.userId], references: [user.id] }),
}))

export const InsertNotificationEventSchema = createInsertSchema(NotificationEvent).omit({
  id: true,
  createdAt: true,
})

export const SelectNotificationEventSchema = createSelectSchema(NotificationEvent)

export const InsertNotificationSchema = createInsertSchema(Notification).omit({
  id: true,
  createdAt: true,
})

export const SelectNotificationSchema = createSelectSchema(Notification)

export type InsertNotificationEvent = typeof NotificationEvent.$inferInsert
export type SelectNotificationEvent = typeof NotificationEvent.$inferSelect
export type InsertNotification = typeof Notification.$inferInsert
export type SelectNotification = typeof Notification.$inferSelect
